/* Category indicator adapted from Build UI Animated Tabs (MIT), 21st.dev.
   Shopify renders the catalog; links and forms remain usable without JS. */
(() => {
  const root = document.querySelector('[data-bb-storefront]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const status = root.querySelector('[data-bb-status]');
  let controller;
  let version = 0;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function catalogKey(destination) {
    const url = new URL(destination, location.href);
    url.searchParams.delete('preview_theme_id');
    url.searchParams.sort();
    return url.pathname + url.search;
  }
  let renderedCatalog = catalogKey(location.href);
  const lifetime = new AbortController();
  const header = document.querySelector('header.shopify-section-header');
  const hero = root.querySelector('.bb-studio__hero');
  const living = root.querySelector('.bb-studio__living-image');
  const rail = root.querySelector('[data-bb-rail]');
  const railControls = root.querySelector('[data-bb-rail-controls]');
  let sceneFrame = null;
  let lastHeaderHeight = -1;

  function updateScene() {
    sceneFrame = null;
    const headerHeight = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    const heroBox = hero?.getBoundingClientRect();
    const livingBox = living?.getBoundingClientRect();
    const nextHeaderHeight = Math.round(headerHeight);
    if (nextHeaderHeight !== lastHeaderHeight) {
      root.style.setProperty('--bb-header-top', `${nextHeaderHeight}px`);
      lastHeaderHeight = nextHeaderHeight;
    }
    const motionEnabled = String(!reduced.matches);
    if (root.dataset.motion !== motionEnabled) root.dataset.motion = motionEnabled;
    if (reduced.matches || document.hidden) {
      root.dataset.heroVisible = 'false';
      root.style.removeProperty('--bb-scene-y');
      root.style.removeProperty('--bb-living-y');
      return;
    }
    if (hero) {
      const box = heroBox;
      const visible = String(box.bottom > 0 && box.top < innerHeight);
      if (root.dataset.heroVisible !== visible) root.dataset.heroVisible = visible;
      if (box.bottom > 0 && box.top < innerHeight) {
        const progress = Math.max(0, Math.min(1, -box.top / Math.max(1,box.height)));
        root.style.setProperty('--bb-scene-y', `${(progress * 18).toFixed(2)}px`);
      }
    }
    if (living) {
      const box = livingBox;
      if (box.bottom > 0 && box.top < innerHeight) {
        const progress = Math.max(-1, Math.min(1, (innerHeight / 2 - box.top - box.height / 2) / innerHeight));
        root.style.setProperty('--bb-living-y', `${(progress * 16).toFixed(2)}px`);
      }
    }
  }
  function scheduleScene() {
    if (sceneFrame === null && !document.hidden) sceneFrame = requestAnimationFrame(updateScene);
  }
  addEventListener('scroll', scheduleScene, {passive:true,signal:lifetime.signal});
  addEventListener('resize', scheduleScene, {signal:lifetime.signal});
  document.addEventListener('visibilitychange', updateScene, {signal:lifetime.signal});
  reduced.addEventListener('change', updateScene, {signal:lifetime.signal});

  function updateRail() {
    if (!rail || !railControls) return;
    const max = rail.scrollWidth - rail.clientWidth;
    railControls.hidden = max <= 2;
    railControls.querySelector('[data-bb-rail-prev]').disabled = rail.scrollLeft <= 2;
    railControls.querySelector('[data-bb-rail-next]').disabled = rail.scrollLeft >= max - 2;
  }
  function railTo(index, focus = false) {
    const item = rail?.children[index];
    if (!item) return;
    if (focus) item.focus({preventScroll:true});
    rail.scrollTo({left:item.offsetLeft - rail.firstElementChild.offsetLeft, behavior:reduced.matches ? 'instant' : 'smooth'});
  }
  if (rail && railControls) {
    const step = direction => {
      const positions = Array.from(rail.children, item => item.offsetLeft - rail.firstElementChild.offsetLeft);
      let index = -1;
      for (let i = 0; i < positions.length; i++) {
        if (direction > 0 && positions[i] > rail.scrollLeft + 2) { index = i; break; }
        if (direction < 0 && positions[i] < rail.scrollLeft - 2) index = i;
      }
      if (index >= 0) railTo(index);
    };
    railControls.querySelector('[data-bb-rail-prev]').addEventListener('click', () => step(-1), {signal:lifetime.signal});
    railControls.querySelector('[data-bb-rail-next]').addEventListener('click', () => step(1), {signal:lifetime.signal});
    rail.addEventListener('scroll', updateRail, {passive:true,signal:lifetime.signal});
    rail.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      const current = Array.from(rail.children).indexOf(event.target.closest('a'));
      if (current < 0) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? rail.children.length - 1 : Math.max(0, Math.min(rail.children.length - 1,current + (event.key === 'ArrowRight' ? 1 : -1)));
      railTo(next, true);
    }, {signal:lifetime.signal});
  }

  // Keep content visible without JS; reveal only offscreen editorial elements.
  if (!reduced.matches && 'IntersectionObserver' in window) {
    const reveal = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('bb-await');
        reveal.unobserve(entry.target);
      });
    }, {rootMargin:'0px 0px 40px 0px', threshold:0.05});
    root.querySelectorAll('[data-bb-reveal]').forEach(element => {
      if (element.getBoundingClientRect().top <= innerHeight) return;
      element.classList.add('bb-await');
      reveal.observe(element);
    });
    const showAll = () => {
      root.querySelectorAll('.bb-await').forEach(element => element.classList.remove('bb-await'));
      reveal.disconnect();
    };
    reduced.addEventListener('change', event => { if (event.matches) showAll(); }, {signal:lifetime.signal});
    root.addEventListener('focusin', event => event.target.closest('[data-bb-reveal]')?.classList.remove('bb-await'), {signal:lifetime.signal});
    lifetime.signal.addEventListener('abort', () => reveal.disconnect(), {once:true});
  }

  function indicator(link, revealActive = false) {
    const tabs = root.querySelector('[data-bb-tabs]');
    const active = link || tabs?.querySelector('[aria-current]');
    const line = tabs?.querySelector('.bb-studio__indicator');
    if (!active || !line) return;
    tabs.dataset.enhanced = 'true';
    line.style.width = `${active.offsetWidth}px`;
    line.style.transform = `translateX(${active.offsetLeft}px)`;
    if (revealActive && tabs.scrollWidth > tabs.clientWidth) tabs.scrollTo?.({left:Math.max(0,active.offsetLeft - (tabs.clientWidth - active.offsetWidth)/2), behavior:reduced.matches ? 'instant' : 'smooth'});
    root.querySelectorAll('.bb-studio__category-tile').forEach(tile => {
      if (new URL(tile.href).pathname === new URL(active.href).pathname) tile.setAttribute('aria-current','page');
      else tile.removeAttribute('aria-current');
    });
  }

  async function navigate(destination, push = true) {
    const url = new URL(destination, location.href);
    if (url.origin !== location.origin) return;
    const requestVersion = ++version;
    controller?.abort();
    controller = new AbortController();
    const catalog = root.querySelector('[data-bb-catalog]');
    catalog.setAttribute('aria-busy', 'true');
    status.textContent = 'Loading objects...';
    const request = new URL(url);
    request.hash = '';
    request.searchParams.set('section_id', 'cx-bigbag-storefront');
    try {
      const response = await fetch(request, {signal: controller.signal});
      if (!response.ok) throw new Error('Catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-bb-catalog]');
      if (!next) throw new Error('Catalog missing');
      if (requestVersion !== version) return;
      const nextHeading = next.querySelector('h1,h2');
      const headingTag = root.querySelector('.bb-studio__hero') ? 'H2' : 'H1';
      if (nextHeading.tagName !== headingTag) {
        const replacement = document.createElement(headingTag);
        replacement.textContent = nextHeading.textContent;
        nextHeading.replaceWith(replacement);
      }
      const focusName = document.activeElement?.getAttribute('name');
      catalog.replaceWith(next);
      // Rebind the theme's existing quick-add controls after section replacement.
      next.dispatchEvent(new CustomEvent('shopify:section:load', {bubbles:true}));
      next.classList.add('is-entering');
      if (push) history.pushState({bigbag: true}, '', url);
      renderedCatalog = catalogKey(url);
      const heading = next.querySelector('h1,h2');
      document.title = `${url.pathname.endsWith('/bigbag') ? 'Home decor' : heading.textContent} | BIGBAG by CRUX`;
      indicator(null, true);
      const control = focusName && Array.from(next.querySelectorAll('[name]')).find(el => el.name === focusName);
      if (control) control.focus({preventScroll:true});
      else { heading.tabIndex = -1; heading.focus({preventScroll:true}); }
      next.scrollIntoView({behavior: reduced.matches ? 'instant' : 'smooth', block:'start'});
      status.textContent = `${next.querySelector('.bb-studio__shop-title > span').textContent.trim()} loaded.`;
    } catch (error) {
      if (error.name === 'AbortError' || requestVersion !== version) return;
      catalog.removeAttribute('aria-busy');
      status.textContent = 'Opening collection...';
      location.assign(url.href);
    }
  }

  root.addEventListener('click', event => {
    const link = event.target.closest('a[data-bb-link]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.closest('[data-bb-tabs]')) indicator(link);
    navigate(link.href);
  }, {signal:lifetime.signal});
  function submit(form) {
    const url = new URL(form.action);
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.hash = 'bb-shop';
    navigate(url.href);
  }
  root.addEventListener('submit', event => {
    if (!event.target.matches('[data-bb-filter]')) return;
    event.preventDefault();
    submit(event.target);
  }, {signal:lifetime.signal});
  root.addEventListener('change', event => {
    const form = event.target.closest('[data-bb-filter]');
    if (form) submit(form);
  }, {signal:lifetime.signal});
  addEventListener('popstate', () => {
    // Native fragment navigation also emits popstate; leave its scroll alone.
    if (/\/collections\/bigbag(?:-[a-z]+)?$/.test(location.pathname) && catalogKey(location.href) !== renderedCatalog) navigate(location.href, false);
  }, {signal:lifetime.signal});
  const layoutObserver = new ResizeObserver(() => { indicator(); updateRail(); scheduleScene(); });
  layoutObserver.observe(root);
  if (header) layoutObserver.observe(header);
  document.addEventListener('shopify:section:unload', event => {
    if (!event.target.contains?.(root)) return;
    ++version;
    controller?.abort();
    lifetime.abort();
    layoutObserver.disconnect();
    if (sceneFrame !== null) cancelAnimationFrame(sceneFrame);
  }, {signal:lifetime.signal});
  document.fonts.ready.then(() => { if (!lifetime.signal.aborted) indicator(); });
  indicator();
  updateRail();
  updateScene();
})();
